import { Logo } from '@litlingo/react';
import { checkLoginRedirect } from 'actions';
import LoadingIndicator from 'components/LoadingIndicator';
import SignInWithGoogleButton from 'components/Login/SignInWithGoogleButton';
import SignInWithLitlingoButton from 'components/Login/SignInWithLitlingoButton';
import SignInWithOffice365Button from 'components/Login/SignInWithOffice365Button';
import SignInWithOktaButton from 'components/Login/SignInWithOktaButton';
import config from 'config';
import { LOGIN_BACKGROUND } from 'constants/common';
import { ERROR_ICON } from 'constants/commonIcons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getAuthError, getIsCheckingLoginRedirect, getIsVerifying } from 'selectors/auth';
import type { ErrorObject } from 'types';
import SignInWithEpiqButton from './SignInWithEpiqButton';

const LOGIN_ACCESS_DENIED_ERROR_CODE = 'login_access_denied';

const LOGIN_ERROR_COPY = {
  accessDenied: {
    title: 'Account not found',
    detail: "We couldn't locate your email. Try a different account, or",
  },
  default: {
    title: 'Unable to sign in',
    detail: 'Please try again, or',
  },
} as const;

const isLoginPageError = (code: string | undefined): boolean =>
  code === 'login' || code === LOGIN_ACCESS_DENIED_ERROR_CODE;

const isAccessDeniedLoginError = (error: ErrorObject): boolean => {
  if (error.code === LOGIN_ACCESS_DENIED_ERROR_CODE) {
    return true;
  }
  const { message } = error;
  if (message == null || message.trim() === '') {
    return false;
  }
  const normalizedMessage = message.toLowerCase();
  return (
    normalizedMessage.includes('could not locate your email') ||
    normalizedMessage.includes('access_denied')
  );
};

type LoginErrorDisplay = {
  title: string;
  detail: string;
  supportPrefix: string;
};

const getLoginErrorDisplay = (error: ErrorObject): LoginErrorDisplay => {
  const template = isAccessDeniedLoginError(error)
    ? LOGIN_ERROR_COPY.accessDenied
    : LOGIN_ERROR_COPY.default;
  const trimmedMessage = error.message?.trim() ?? '';

  if (trimmedMessage !== '') {
    return {
      title: template.title,
      detail: trimmedMessage,
      supportPrefix: ' Or ',
    };
  }

  return {
    title: template.title,
    detail: template.detail,
    supportPrefix: '',
  };
};

const Login: React.FC = () => {
  const location = useLocation();

  const dispatch = useDispatch();
  const isVerifying = useSelector(getIsVerifying);
  const isCheckingLoginRedirect = useSelector(getIsCheckingLoginRedirect);
  const loading = isVerifying || isCheckingLoginRedirect;
  const error = useSelector(getAuthError);

  const [isEmailInputVisible, setIsEmailInputVisible] = useState<boolean>(false);

  useEffect(() => {
    if (location.search !== '') dispatch(checkLoginRedirect());
  }, [dispatch, location.hash, location.search]);

  const hasLoginError = error != null && isLoginPageError(error.code) && !loading;

  const renderCenteredContent = (): JSX.Element => {
    if (loading) {
      return <LoadingIndicator size="20" />;
    }

    const loginErrorDisplay =
      error != null && isLoginPageError(error.code) ? getLoginErrorDisplay(error) : null;

    return (
      <>
        {!isEmailInputVisible && (
          <div className="my-1 w-full">
            <SignInWithGoogleButton />
          </div>
        )}
        {!isEmailInputVisible && (
          <div className="my-1 w-full">
            <SignInWithOffice365Button />
          </div>
        )}
        {!isEmailInputVisible && (
          <div className="my-1 w-full">
            <SignInWithEpiqButton />
          </div>
        )}

        <div className="my-1 w-full">
          <SignInWithOktaButton {...{ isEmailInputVisible, setIsEmailInputVisible }} />
        </div>
        {isEmailInputVisible && (
          <div
            className="my-1 w-full"
            onClick={(): void => {
              setIsEmailInputVisible(false);
            }}
            onKeyDown={(e): void => {
              if (e.key === 'Enter') {
                setIsEmailInputVisible(false);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span className="text text-litlingo-info cursor-pointer underline font-normal">
              {'< Back'}
            </span>
          </div>
        )}

        <div className="my-1 w-full hidden">
          <SignInWithLitlingoButton />
        </div>

        {loginErrorDisplay && (
          <div className="flex flex-col items-center error-bg-opacity py-3 px-4 w-full mt-4">
            <div className="flex items-center justify-center">
              <span className="mr-2">{ERROR_ICON}</span>
              <span className="text text--bold text-litlingo-error">{loginErrorDisplay.title}</span>
            </div>
            <span className="text text-litlingo-error text-center mt-2 w-full">
              {loginErrorDisplay.detail}
              {loginErrorDisplay.supportPrefix || ' '}
              <a
                href="mailto:support@litlingo.com"
                className="text text-litlingo-info hover:underline font-normal"
              >
                contact support
              </a>
            </span>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="relative min-h-screen bg-white flex flex-col justify-center items-center">
      <div
        className={`flex flex-col items-center rounded-sm shadow-login-card bg-litlingo-off-white login-card px-10 py-10 z-10 h-auto ${
          hasLoginError ? 'justify-start' : 'justify-center'
        }`}
      >
        <div
          className={`flex flex-col justify-center items-center w-4/5 ${
            hasLoginError ? 'mb-8' : 'mb-12'
          }`}
        >
          <Logo />
        </div>
        <div className="flex flex-col items-center w-4/5 shrink-0">
          {renderCenteredContent()}
          <span className="text text-center mt-6 w-4/5 font-normal">
            By signing in you agree to LitLingo’s{' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={config.privacy.url}
              className="text underline text-litlingo-info cursor-pointer font-normal"
            >
              Privacy Policy
            </a>
          </span>
        </div>
      </div>
      <div className="mt-6 z-10">
        <span className="text font-normal">Interested in mitigating risk for your business? </span>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={config.demo.url}
          className="text text-litlingo-info cursor-pointer underline font-normal"
        >
          See a demo
        </a>
      </div>
      <div className="absolute bottom-0 h-full w-full z-0">{LOGIN_BACKGROUND}</div>
    </div>
  );
};

export default Login;
